import React from 'react';
import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { LocalShipping as LocalShippingIcon } from '@mui/icons-material';
import type { MonitoringDeliveryUsage } from '../../types/monitoring';
import { resolveMonitoringImageUrl } from '../../utils/monitoringPresentation';
import { formatEuro } from './MonitoringStatsPanel';

type Props = {
  usage?: MonitoringDeliveryUsage | null;
  periodLabel?: string;
};

const MonitoringDeliveryUsagePanel: React.FC<Props> = ({ usage, periodLabel }) => {
  const carriers = usage?.carriers ?? [];
  const total = usage?.total ?? 0;

  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.25 }}>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <LocalShippingIcon sx={{ fontSize: 18, color: '#0f766e' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Livraisons par transporteur
            </Typography>
          </Stack>
          <Chip
            size="small"
            label={`${total} livraison${total > 1 ? 's' : ''}`}
            sx={{ fontWeight: 700, bgcolor: 'rgba(15, 118, 110, 0.1)', color: '#0f766e' }}
          />
        </Stack>

        {periodLabel ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
            {periodLabel}
            {usage ? ` · ${formatEuro(usage.revenue_total_eur)} encaissés` : ''}
          </Typography>
        ) : null}

        <Stack spacing={0.9}>
          {carriers.map((carrier) => {
            const logoSrc = resolveMonitoringImageUrl(carrier.logo_url);
            const isInactive = !carrier.active;

            return (
              <Box
                key={carrier.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 1,
                  py: 0.85,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: isInactive ? 'rgba(148, 163, 184, 0.35)' : 'divider',
                  bgcolor: isInactive ? 'rgba(248, 250, 252, 0.9)' : 'background.paper',
                  opacity: isInactive ? 0.72 : 1,
                }}
              >
                <Box
                  sx={{
                    width: 72,
                    height: 28,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {logoSrc ? (
                    <Box
                      component="img"
                      src={logoSrc}
                      alt={carrier.label}
                      sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    <Typography variant="caption" sx={{ fontWeight: 700, textAlign: 'center' }}>
                      {carrier.label}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    {carrier.label}
                  </Typography>
                  {isInactive ? (
                    <Typography variant="caption" color="text.secondary">
                      Intégration à venir
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {formatEuro(carrier.revenue_eur)} encaissés
                    </Typography>
                  )}
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    color: isInactive ? 'text.disabled' : '#0f766e',
                    minWidth: 28,
                    textAlign: 'right',
                  }}
                >
                  {carrier.count}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default MonitoringDeliveryUsagePanel;
